import type { Updater } from '@tanstack/vue-table';
import { clsx } from 'clsx';
import type { ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Ref } from 'vue';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function valueUpdater<T extends Updater<any>>(updaterOrValue: T, ref: Ref) {
  ref.value = typeof updaterOrValue === 'function' ? updaterOrValue(ref.value) : updaterOrValue;
}

export function downloadBlob(data: any, filename: string, type: string) {
  const blob = new Blob([data], { type: `${type};charset=utf-8;` });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export function downloadFile(uri: string, filename: string = '') {
  const anchor = document.createElement('a');
  anchor.download = filename;
  anchor.href = uri;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export async function downloadFileAsync(uri: string, filename: string = ''): Promise<void> {
  return fetch(uri, {
    headers: {
      'Content-Type': 'application/octet-stream',
    },
    mode: 'no-cors',
  })
    .then((response) => response.blob())
    .then((data) => downloadFile(window.URL.createObjectURL(data), filename));
}

export function isValidDomain(domain: string): boolean {
  return /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/.test(domain);
}

export function isEmailValid(email: string | null): boolean {
  // Thanks to:
  // http://fightingforalostcause.net/misc/2006/compare-email-regex.php
  // http://thedailywtf.com/Articles/Validating_Email_Addresses.aspx
  // http://stackoverflow.com/questions/201323/what-is-the-best-regular-expression-for-validating-email-addresses/201378#201378
  // https://en.wikipedia.org/wiki/Email_address  The format of an email address is local-part@domain, where the
  // local part may be up to 64 octets long and the domain may have a maximum of 255 octets.[4]
  if (!email) return false;

  const emailParts = email.split('@');

  if (emailParts.length !== 2) return false;

  const account = emailParts[0];
  const address = emailParts[1];

  if (account.length > 64) return false;
  else if (address.length > 255) return false;

  const domainParts = address.split('.');

  if (domainParts.some((part) => part.length > 63)) return false;

  const emailExpr =
    /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](?:\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](?:-*\.?[a-zA-Z0-9])*\.[a-zA-Z](?:-?[a-zA-Z0-9])+$/;

  return emailExpr.test(email);
}

export function plaintextToHtml(plaintext: string): string {
  // Split the plaintext into paragraphs
  const paragraphs = plaintext.trim().split(/\n{2,}/);

  // Wrap each paragraph in <p> tags and replace single newlines with <br> tags
  const html = paragraphs
    .map((paragraph) => {
      const lines = paragraph.split('\n').map((line) => line.trim());
      return `${lines.join('<br>')}`;
    })
    .join('<br>');

  return html;
}

export function popupCenter(url: string, title: string, w: number, h: number) {
  // Fixes dual-screen position                             Most browsers      Firefox
  const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
  const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

  const width = window.innerWidth
    ? window.innerWidth
    : document.documentElement.clientWidth
      ? document.documentElement.clientWidth
      : screen.width;
  const height = window.innerHeight
    ? window.innerHeight
    : document.documentElement.clientHeight
      ? document.documentElement.clientHeight
      : screen.height;

  const systemZoom = width / window.screen.availWidth;
  const left = (width - w) / 2 / systemZoom + dualScreenLeft;
  const top = (height - h) / 2 / systemZoom + dualScreenTop;
  return window.open(
    url,
    title,
    `
      scrollbars=yes,
      width=${w / systemZoom}, 
      height=${h / systemZoom}, 
      top=${top}, 
      left=${left}
      `,
  );
}

export function withLoading(loadingRef: Ref) {
  return function (fn) {
    return async function (...args) {
      loadingRef.value = true;
      try {
        return await fn(...args);
      } finally {
        loadingRef.value = false;
      }
    };
  };
}
